import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/firestore";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import * as firebase from "firebase";
import { Examen } from "../../models/examen";
import { Ordonnance } from "../../models/ordonnance";

/** Filtres de la liste des examens (chaîne vide = filtre inactif). */
export interface ExamenFilters {
	date: string;       // yyyy-MM-dd, égalité
	cage: string;       // « commence par », majuscules respectées
	entreprise: string; // « commence par »
	ecloserie: string;  // « commence par »
	diagnostic: string; // libellé exact (le champ est une liste)
}

export interface ExamenChange {
	action: "add" | "update" | "delete";
	examen?: Examen;
	id?: string;
}

const DEFAULT_NO_FINDING = "Rien à signaler";

const EXAMEN_FIELDS = [
	"bacteriologique",
	"parasitaire",
	"dateExamen",
	"entreprise",
	"numCage",
	"espece",
	"ecloserie",
	"tempEau",
	"dateRecepetion",
	"pm",
	"mortaliteparjours",
	"nbrJoursMortalite",
	"totalMortalite",
	"incident",
	"embonpointobservation",
	"embonpointobservationinput",
	"embonpointfrequence",
	"embonpointseverite",
	"revetementobservation",
	"revetementobservationinput",
	"revetementfrequence",
	"revetementseverite",
	"nageoiresobservation",
	"nageoiresobservationinput",
	"nageoiresfrequence",
	"nageoiresseverite",
	"operculeobservation",
	"operculeobservationinput",
	"operculefrequence",
	"operculeseverite",
	"boucheobservation",
	"boucheobservationinput",
	"bouchefrequence",
	"boucheseverite",
	"estomacobservation",
	"estomacobservationinput",
	"estomacfrequence",
	"estomacseverite",
	"intestinobservation",
	"intestinobservationinput",
	"intestinfrequence",
	"intestinseverite",
	"branchieobservation",
	"branchieobservationinput",
	"branchiefrequence",
	"branchieseverite",
	"coeurobservation",
	"coeurobservationinput",
	"coeurfrequence",
	"coeurseverite",
	"teteobservation",
	"teteobservationinput",
	"tetefrequence",
	"teteseverite",
	"yeuxobservation",
	"yeuxobservationinput",
	"yeuxfrequence",
	"yeuxseverite",
	"cerveauobservation",
	"cerveauobservationinput",
	"cerveaufrequence",
	"cerveauseverite",
	"rateobservation",
	"rateobservationinput",
	"ratefrequence",
	"rateseverite",
	"foieobservation",
	"foieobservationinput",
	"foiefrequence",
	"foieseverite",
	"vesiculeBiliaireobservation",
	"vesiculeBiliaireobservationinput",
	"vesiculeBiliairefrequence",
	"vesiculeBiliaireseverite",
	"reinobservation",
	"reinobservationinput",
	"reinfrequence",
	"reinseverite",
	"rechercheparasitaireobservation",
	"rechercheparasitaireforme",
	"recherchebacteriologiqueobservation",
	"recherchebacteriologiqueforme",
	"vessienatatoireobservation",
	"vessienatatoireobservationinput",
	"vessienatatoirefrequence",
	"vessienatatoireseverite",
	"autresobservation",
	"autresobservationinput",
	"autresfrequence",
	"autresseverite",
	"diagnosticdiagnostictexte",
	"diagnosticobservation",
	"diagnosticforme",
	"conduiteconduitetexte",
	"conduiteobservation",
	"conduitemolecule",
	"conduitedurretraitement",
];

const EXAMEN_IMAGE_FIELDS = [
	"imageurl",
	"imageurlconvert",
	"imageurl1",
	"imageurlconvert1",
	"imageurl2",
	"imageurlconvert2",
	"imageurl3",
	"imageurlconvert3",
	"imageurl4",
	"imageurlconvert4",
];

const COLLECTIONS = {
	autres: "autres",
	bacteriologique: "Bacteriologique",
	bouche: "Bouche",
	branchie: "Branchie",
	cerveau: "Cerveau",
	coeur: "Coeur",
	conduite: "Conduites",
	diagnostic: "diagnostics",
	ecloserie: "Ecloseries",
	embonpoint: "Embonpoints",
	entreprise: "Entreprises",
	espece: "Especes",
	estomac: "Estomac",
	foie: "Foie",
	incident: "Incidents",
	intestin: "Intestin",
	nageoires: "Nageoires",
	opercule: "Opercule",
	parasitaire: "Parasitaire",
	rate: "Rate",
	rein: "Rein",
	revetement: "Revetementcutane",
	tete: "Tete",
	vesiculebiliaire: "Vesiculebiliaire",
	vessienatatoire: "Vessienatatoire",
	yeux: "Yeux",
	molecule: "Molecules",
	dureetraitement: "Dureetraitements",
	medicament: "Medicaments",
};

@Injectable({
	providedIn: "root",
})
export class ConduiteService {
	private myDate = new Date();

	private examenChanges = new Subject<ExamenChange>();
	examenChanges$: Observable<ExamenChange> = this.examenChanges.asObservable();

	constructor(
		private firestore: AngularFirestore,
		private datePipe: DatePipe,
	) {}

	private list(collectionName: string) {
		return this.firestore.collection(collectionName).snapshotChanges();
	}

	private normalizeLibelle(libelle: string): string {
		return (libelle || "").trim();
	}

	private buildExamenPayload(data: any, withModificationDate = false) {
		const payload = {};

		if (withModificationDate) {
			payload["dateModification"] = this.datePipe.transform(this.myDate, "yyyy-MM-dd");
		}

		EXAMEN_FIELDS.forEach((field) => {
			payload[field] = data[field];
		});

		EXAMEN_IMAGE_FIELDS.forEach((field) => {
			payload[field] = data[field] === "" ? "" : data[field];
		});

		// Depuis le 2026-09-20 « Mortalité totale » est enregistrée correctement (avant, elle recevait le nombre de
		// jours de mortalité). Seuls les examens portant ce marqueur comptent dans la mortalité cumulée du dashboard.
		payload["mortaliteFiable"] = true;

		return payload;
	}

	getLibelleList(collectionName: string) {
		return this.list(collectionName);
	}

	saveLibelleItem(collectionName: string, libelle: string): Promise<any> {
		const cleanLibelle = this.normalizeLibelle(libelle);
		return this.firestore.collection(collectionName).add({ libelle: cleanLibelle });
	}

	updateLibelleItem(collectionName: string, id: string, libelle: string) {
		return this.firestore.collection(collectionName).doc(id).set({
			libelle: this.normalizeLibelle(libelle),
		});
	}

	deleteLibelleItem(collectionName: string, id: string) {
		return this.firestore.doc(collectionName + "/" + id).delete();
	}

	ensureLibelleItem(collectionName: string, libelle: string): Promise<any> {
		const cleanLibelle = this.normalizeLibelle(libelle);

		if (!cleanLibelle || cleanLibelle === DEFAULT_NO_FINDING) {
			return Promise.resolve(null);
		}

		return this.firestore
			.collection(collectionName)
			.ref
			.where("libelle", "==", cleanLibelle)
			.limit(1)
			.get()
			.then((snapshot) => {
				if (!snapshot.empty) {
					return snapshot.docs[0].ref;
				}

				return this.saveLibelleItem(collectionName, cleanLibelle);
			});
	}

	getListparasitaire() { return this.list(COLLECTIONS.parasitaire); }
	getListbacteriologique() { return this.list(COLLECTIONS.bacteriologique); }
	getListConduite() { return this.list(COLLECTIONS.conduite); }
	getListEntreprise() { return this.list(COLLECTIONS.entreprise); }
	getListEcloseries() { return this.list(COLLECTIONS.ecloserie); }
	getListIncidents() { return this.list(COLLECTIONS.incident); }
	getListEspeces() { return this.list(COLLECTIONS.espece); }
	getListEmbonpoints() { return this.list(COLLECTIONS.embonpoint); }
	getListdiagnostic() { return this.list(COLLECTIONS.diagnostic); }
	getListautres() { return this.list(COLLECTIONS.autres); }
	getListRevetementcutane() { return this.list(COLLECTIONS.revetement); }
	getListNageoires() { return this.list(COLLECTIONS.nageoires); }
	getListOpercule() { return this.list(COLLECTIONS.opercule); }
	getListBouche() { return this.list(COLLECTIONS.bouche); }
	getListEstomac() { return this.list(COLLECTIONS.estomac); }
	getListIntestin() { return this.list(COLLECTIONS.intestin); }
	getListBranchie() { return this.list(COLLECTIONS.branchie); }
	getListCoeur() { return this.list(COLLECTIONS.coeur); }
	getListTete() { return this.list(COLLECTIONS.tete); }
	getListYeux() { return this.list(COLLECTIONS.yeux); }
	getListCerveau() { return this.list(COLLECTIONS.cerveau); }
	getListRate() { return this.list(COLLECTIONS.rate); }
	getListFoie() { return this.list(COLLECTIONS.foie); }
	getListVesiculebiliaire() { return this.list(COLLECTIONS.vesiculebiliaire); }
	getListRein() { return this.list(COLLECTIONS.rein); }
	getListVessienatatoire() { return this.list(COLLECTIONS.vessienatatoire); }
	getListMolecules() { return this.list(COLLECTIONS.molecule); }
	getListDureetraitements() { return this.list(COLLECTIONS.dureetraitement); }
	getListMedicaments() { return this.list(COLLECTIONS.medicament); }

	getListOrdonnances() {
		return this.firestore
			.collection("ordonnances", ref => ref.orderBy("dateOrdonnance", "desc"))
			.snapshotChanges();
	}

	saveOrdonnance(ordonnance: Ordonnance): Promise<any> {
		const { id, ...payload } = ordonnance;
		return this.firestore.collection("ordonnances").add({
			...payload,
			dateCreation: this.datePipe.transform(new Date(), "yyyy-MM-dd"),
		});
	}

	updateOrdonnance(id: string, ordonnance: Ordonnance): Promise<void> {
		const { id: _ignored, ...payload } = ordonnance;
		// merge : une modification ne doit pas effacer les champs que le formulaire ne connaît pas
		// (dateCreation, examenId de l'examen d'origine)
		return this.firestore.collection("ordonnances").doc(id).set(payload, { merge: true });
	}

	deleteOrdonnance(id: string): Promise<void> {
		return this.firestore.collection("ordonnances").doc(id).delete();
	}

	getListexamen() {
		return this.firestore
			.collection("examens", ref => ref.orderBy("dateExamen", "desc"))
			.snapshotChanges();
	}

	/**
	 * Une page d'examens, lue une seule fois. On utilise get() et non snapshotChanges() : avec le cache
	 * local Firestore (enablePersistence), la première émission d'un snapshotChanges() vient du cache et
	 * ne contient que les documents déjà vus (les 5 du dashboard) — combinée à take(1), la liste
	 * n'affichait que 5 examens. get() interroge le serveur (et ne se rabat sur le cache qu'hors ligne).
	 * On garde la forme { payload: { doc } } attendue par ExamenComponent.
	 */
	/**
	 * Recherche sur TOUS les examens (pas seulement la page affichée). Firestore n'accepte qu'un filtre
	 * de plage/tri par requête sans index composite : on interroge sur le premier filtre actif
	 * (cage > entreprise > écloserie > date > diagnostic), puis l'appelant affine avec les autres.
	 * « Commence par » : plage [texte, texte + \uf8ff], donc sensible aux majuscules.
	 */
	searchExamens(f: ExamenFilters, limit: number): Observable<any[]> {
		const t = (v: string) => (v || "").trim();
		return this.firestore
			.collection("examens", ref => {
				let q: firebase.firestore.Query = ref;
				if (t(f.cage)) {
					q = q.orderBy("numCage").startAt(t(f.cage)).endAt(t(f.cage) + "\uf8ff");
				} else if (t(f.entreprise)) {
					q = q.orderBy("entreprise").startAt(t(f.entreprise)).endAt(t(f.entreprise) + "\uf8ff");
				} else if (t(f.ecloserie)) {
					q = q.orderBy("ecloserie").startAt(t(f.ecloserie)).endAt(t(f.ecloserie) + "\uf8ff");
				} else if (t(f.date)) {
					q = q.where("dateExamen", "==", t(f.date));
				} else if (t(f.diagnostic)) {
					q = q.where("diagnosticobservation", "array-contains", t(f.diagnostic));
				}
				return q.limit(limit);
			})
			.get()
			.pipe(map(snapshot => snapshot.docs.map(doc => ({ ...(doc.data() as any), id: doc.id }))));
	}

	getListexamenPage(pageSize: number, cursor?: any) {
		return this.firestore
			.collection("examens", ref => {
				let q = ref.orderBy("dateExamen", "desc").limit(pageSize);
				return cursor ? q.startAfter(cursor) : q;
			})
			.get()
			.pipe(map(snapshot => snapshot.docs.map(doc => ({ payload: { doc } }))));
	}

	getexamensbyid(id: string): Observable<any> {
		return this.firestore
			.collection("examens")
			.doc(id)
			.valueChanges();
	}

	saveExamen(exam: Examen): Promise<any> {
		const payload = this.buildExamenPayload(exam);
		return this.firestore.collection("examens").add(payload).then(docRef => {
			this.updateDashboardStats(payload, null);
			this.examenChanges.next({ action: "add", examen: { ...payload, id: docRef.id } as Examen });
			return docRef;
		});
	}

	updateExamen(data: any, oldExamen?: any) {
		const payload = this.buildExamenPayload(data, true);
		const old$ = oldExamen ? Promise.resolve(oldExamen) : this.getExamenData(data.id);
		return old$.then(oldData =>
			this.firestore.collection("examens").doc(data.id).set(payload).then(() => {
				this.updateDashboardStats(payload, oldData);
				this.examenChanges.next({ action: "update", examen: { ...payload, id: data.id } as Examen });
			})
		);
	}

	deleteExamen(id: string, oldExamen?: any) {
		const old$ = oldExamen ? Promise.resolve(oldExamen) : this.getExamenData(id);
		return old$.then(oldData =>
			this.deleteLibelleItem("examens", id).then(() => {
				this.updateDashboardStats(null, oldData);
				this.deleteExamenImages(oldData);
				this.examenChanges.next({ action: "delete", id });
			})
		);
	}

	/**
	 * Supprime de Firebase Storage les photos originales d'un examen supprimé (imageurl…imageurl4) : sans cela
	 * elles restaient orphelines. Les valeurs qui ne sont pas des URL Storage (anciens examens, base64) sont ignorées,
	 * et un échec n'empêche pas la suppression de l'examen.
	 */
	private deleteExamenImages(examen: any): void {
		["imageurl", "imageurl1", "imageurl2", "imageurl3", "imageurl4"].forEach(champ => {
			const url = examen && examen[champ];
			if (typeof url === "string" && url.indexOf("firebasestorage.googleapis.com") !== -1) {
				firebase.storage().refFromURL(url).delete()
					.catch(err => console.error("Suppression de l'image impossible", champ, err));
			}
		});
	}

	// ─── Statistiques dashboard — doc unique stats/dashboard, maintenu à
	//     l'écriture (FieldValue.increment) pour éviter de relire la collection ───

	getDashboardStats(): Observable<any> {
		return this.firestore.doc("stats/dashboard").valueChanges();
	}

	getLastExamens(count: number) {
		return this.firestore
			.collection("examens", ref => ref.orderBy("dateExamen", "desc").limit(count))
			.snapshotChanges();
	}

	private getExamenData(id: string): Promise<any> {
		return this.firestore.collection("examens").doc(id).ref.get()
			.then(snap => (snap.exists ? snap.data() : null))
			.catch(() => null);
	}

	private examenStatsValues(data: any): { mois: string; mortalite: number } {
		const mois = String((data && data.dateExamen) || "").substring(0, 7);
		const fiable = !!(data && data.mortaliteFiable === true);
		const mortalite = fiable ? parseInt(data.totalMortalite, 10) : NaN;
		return { mois, mortalite: isNaN(mortalite) ? 0 : mortalite };
	}

	private updateDashboardStats(newData: any, oldData: any): Promise<void> {
		const inc = firebase.firestore.FieldValue.increment;
		let mortaliteDelta = 0;
		const parMois: { [mois: string]: number } = {};
		if (newData) {
			const v = this.examenStatsValues(newData);
			mortaliteDelta += v.mortalite;
			if (v.mois) { parMois[v.mois] = (parMois[v.mois] || 0) + 1; }
		}
		if (oldData) {
			const v = this.examenStatsValues(oldData);
			mortaliteDelta -= v.mortalite;
			if (v.mois) { parMois[v.mois] = (parMois[v.mois] || 0) - 1; }
		}
		const update: any = {
			totalExamens: inc((newData ? 1 : 0) - (oldData ? 1 : 0)),
			mortaliteTotale: inc(mortaliteDelta),
		};
		const moisKeys = Object.keys(parMois).filter(m => parMois[m] !== 0);
		if (moisKeys.length) {
			update.examensParMois = {};
			moisKeys.forEach(m => update.examensParMois[m] = inc(parMois[m]));
		}
		return this.firestore.doc("stats/dashboard").ref
			.set(update, { merge: true })
			.catch(err => console.error("Erreur mise à jour des stats dashboard", err));
	}
}
